import { FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  IdentityA11yAnnouncer,
  IdentityButton,
  IdentityFooterInline,
  IdentityHero,
  IdentityLayout,
  IdentityVerificationBanner,
  IDENTITY_BUTTON_LABELS,
  IdentityError as IdentityErrorView,
} from "@speedycore/identity-ui";
import {
  identityRequestVerifyEmail,
  sendIdentityTelemetry,
} from "../lib/identityApi";

export function VerifyEmailResendPage() {
  const [params] = useSearchParams();
  const prefill = useMemo(() => params.get("email") || "", [params]);
  const [email, setEmail] = useState(prefill);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [a11y, setA11y] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const target = email.trim().toLowerCase();
    setBusy(true);
    setError(null);
    setSent(false);
    try {
      await identityRequestVerifyEmail(target);
      setSent(true);
      setA11y("Verification email sent.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not send verification email.";
      setError(msg);
      setA11y(msg);
      void sendIdentityTelemetry("identity.permission_denied", {
        error_code: "verify_resend_failed",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <IdentityA11yAnnouncer message={a11y} type={error ? "error" : "status"} />
      <IdentityLayout
        hero={
          <IdentityHero
            title="Resend verification email"
            subtitle="Enter your email and we'll send a fresh verification link."
          />
        }
        form={
          <>
            {sent ? (
              <IdentityVerificationBanner
                tone="success"
                title="Verification email sent"
                message="Check your inbox for the link. It will expire in 15 minutes."
              />
            ) : null}
            {error && !sent ? (
              <IdentityVerificationBanner tone="error" title="Unable to send" message={error} />
            ) : null}
            {!sent ? (
              <form className="sc-identity-form" onSubmit={(e) => void onSubmit(e)} noValidate>
                <label>
                  <span className="sc-identity-field-label">Email</span>
                  <input
                    type="email"
                    inputMode="email"
                    name="email"
                    id="identity-email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    aria-required
                    aria-describedby="identity-email-help"
                    placeholder="you@example.com"
                  />
                  <span id="identity-email-help" className="sc-identity-field-hint" aria-hidden>
                    We'll send login links and verification emails here.
                  </span>
                </label>
                {error ? <IdentityErrorView message={error} /> : null}
                <IdentityButton
                  type="submit"
                  label={IDENTITY_BUTTON_LABELS.resendVerification}
                  loading={busy}
                />
              </form>
            ) : null}
            <div className="sc-identity-links" style={{ marginTop: "1rem" }}>
              <Link to="/auth/login">Back to sign in</Link>
              <Link to="/auth/verify-email">Open verification link</Link>
            </div>
            <IdentityFooterInline />
          </>
        }
      />
    </>
  );
}
